import clsx from "clsx";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from "react";
import { Link } from "react-router-dom";
import { Asset, isThor, Msg, MsgRedactedBase } from "rujira.js";
import {
  Animated,
  Button,
  Icons,
  TxButton as TxButtonInner,
  TxButtonProps as TxButtonPropsInner,
  useLocale,
  usePrivateModeRecoveryModal,
  useTranslation,
  Warning,
} from "rujira.ui";

import { useWalletAccounts } from "../../services/accounts";
import { usePendingDepositStore } from "../../services/deposits";
import {
  privateActionNamesConnectedWallet,
  translatePrivateMessage,
} from "../../services/privateMessage";
import { useRedacted } from "../../services/redacted";
import { QUERY_CLIENT } from "../../services/queryClient";
import { useRedactedAccountProvider } from "../../redacted/tx";
import { useRedactedV2 } from "../../redacted/v2";
import { useBalanceAccount, useDeposit } from "./Deposit";
import { PrivateFeeReview } from "./PrivateFeeReview";
import { PrivateOperationSteps } from "./PrivateOperationSteps";
import {
  PrivateToastErrorGhost,
  PrivateToastGhost,
  PrivateToastSuccessGhost,
} from "./PrivateToastGhost";
import { PrivateActionButton } from "./PrivateActionButton";
import { useStablePrivateMessage } from "./useStablePrivateMessage";

export type TxButtonProps = Omit<
  TxButtonPropsInner,
  "signer" | "accountProvider"
> & {
  /** Submit a deliberately public funding transaction while Private Mode is on. */
  submitPublic?: boolean;
};

const context = createContext<Msg | null>(null);

/**
 * MsgProvider is a composable interface to provide a Msg value for a TxButton
 * Nested MsgProviders can adjust the msg finally accessed by the TxButton
 */
export const MsgProvider: FC<PropsWithChildren<{ msg: Msg | null }>> = ({
  children,
  msg,
}) => {
  return <context.Provider value={msg}>{children}</context.Provider>;
};

export const useMsg = () => useContext(context);

/**
 * Whether the transaction of this MsgProvider pays a network fee, so a form
 * that shows the fee from its TxButton's simulation has one to show. False
 * when the transaction layer relays the action (Private Mode, or a message
 * that is private itself): its TxButton then shows the fee the user pays,
 * right above the button, from before any quote until the transaction is
 * done. Not for a TxButton with submitPublic, which always pays one.
 */
export const usePaysNetworkFee = () => {
  const msg = useMsg();
  const { isPrivateMode } = useRedacted();
  const v2 = useRedactedV2();
  return !isPrivateMode && !(v2.enabled && msg instanceof MsgRedactedBase);
};

const PrivateSessionUnlockButton: FC<{
  className?: string;
  style?: TxButtonProps["style"];
  controls: ReturnType<typeof useRedactedV2>;
}> = ({ className, style, controls }) => {
  const openUnlock = usePrivateModeRecoveryModal(controls);
  // Open the unlock right here: the expiry notice waits until every dialog
  // is closed, so inside a dialog this button would otherwise do nothing.
  const requestUnlock = () => {
    if (openUnlock("login", false, { loginVariant: "session-expired" }))
      controls.dismissSessionExpired();
    else controls.requestSessionUnlock();
  };
  const classes = className?.split(/\s+/) ?? [];
  // Preserve the existing dimensions of the small order/portfolio controls.
  // A full text label in their w-3 cells would expand and distort the table.
  const compact =
    classes.includes("transparent") &&
    classes.some((value) => /^w-\d+(?:\.\d+)?$/.test(value));
  return (
    <Button
      type="button"
      className={className}
      style={style}
      onClick={requestUnlock}
      aria-label="Unlock Private Mode"
      aria-haspopup="dialog"
      data-tooltip-id={compact ? "global-tip" : undefined}
      data-tooltip-content={compact ? "Unlock Private Mode" : undefined}
      label={compact ? undefined : "Unlock Private Mode"}>
      {compact && <Icons.Locked className="w-full h-full" aria-hidden="true" />}
    </Button>
  );
};

export const TxButton: FC<Omit<TxButtonProps, "msg">> = ({
  children,
  onSuccess,
  submitPublic = false,
  ...props
}) => {
  const { t } = useTranslation("common");
  const rawMsg = useMsg();
  rawMsg?.withQueryClient?.(QUERY_CLIENT);
  const accountProvider = useWalletAccounts();
  const redactedAccountProvider = useRedactedAccountProvider(accountProvider);
  const v2 = useRedactedV2();
  const { isPrivateMode, stealth, publicAddress, subWalletAddress } =
    useRedacted();
  const [setDeposit] = usePendingDepositStore();
  const { toRoot } = useLocale();
  const connectTo = toRoot("connect");
  const directV2Message = !!rawMsg && v2.isV2DirectMessage(rawMsg);
  const connectedKey = [
    publicAddress,
    ...(accountProvider.accounts ?? []).map(
      (account) => account.address.address
    ),
  ].join("\n");

  const {
    msg: translatedMsg,
    privacyUnsupported,
    linksConnectedWallet,
  } = useMemo(() => {
    const unchanged = {
      privacyUnsupported: false,
      linksConnectedWallet: false,
    };
    if (!rawMsg || !isPrivateMode || submitPublic) {
      return { ...unchanged, msg: rawMsg };
    }
    if (directV2Message) {
      return { ...unchanged, msg: rawMsg };
    }
    if (!stealth || !publicAddress || !subWalletAddress) {
      return { ...unchanged, msg: null, privacyUnsupported: true };
    }
    // Never from the Spending account to a connected wallet, whichever
    // form built the message.
    const checked = (privateMsg: Msg) =>
      privateActionNamesConnectedWallet(
        privateMsg,
        connectedKey.split("\n").filter(Boolean)
      )
        ? { ...unchanged, msg: null, linksConnectedWallet: true }
        : { ...unchanged, msg: privateMsg };
    if (rawMsg instanceof MsgRedactedBase) return checked(rawMsg);
    try {
      const privateMsg = translatePrivateMessage(rawMsg, v2.enabled, {
        stealth,
        publicAddress,
        subWalletAddress,
      });
      return privateMsg instanceof MsgRedactedBase
        ? checked(privateMsg)
        : { ...unchanged, msg: null, privacyUnsupported: true };
    } catch {
      return { ...unchanged, msg: null, privacyUnsupported: true };
    }
  }, [
    connectedKey,
    isPrivateMode,
    publicAddress,
    rawMsg,
    stealth,
    subWalletAddress,
    submitPublic,
    directV2Message,
    v2.enabled,
  ]);
  const privateScope =
    v2.enabled && isPrivateMode && !submitPublic
      ? JSON.stringify([
          v2.activeAccountId,
          v2.status,
          accountProvider.selected?.provider,
          accountProvider.selected?.address.address,
          publicAddress,
          subWalletAddress,
          stealth,
        ])
      : undefined;
  const msg = useStablePrivateMessage(translatedMsg, privateScope);
  // A quote is only valid for the account and session it was made for: a
  // wallet, session or public/private switch needs a new one even when the
  // message's own data is identical.
  const simulationScope =
    submitPublic || !isPrivateMode
      ? `public:${accountProvider.selected?.provider ?? ""}`
      : (privateScope ?? "private");
  const reviewPrivateFees =
    v2.enabled && !submitPublic && msg instanceof MsgRedactedBase;
  const privateOperationInProgress =
    v2.operation?.phase === "preparing" ||
    v2.operation?.phase === "submitting" ||
    v2.operation?.phase === "pending";
  const TransactionButton =
    reviewPrivateFees && props.className?.split(/\s+/).includes("transparent")
      ? PrivateActionButton
      : TxButtonInner;

  // A locked private session must never fall through to a public transaction
  // or the unsupported-private warning, even while its wallet is restoring.
  if (!submitPublic && v2.privateSessionLocked) {
    return (
      <PrivateSessionUnlockButton
        className={props.className}
        style={props.style}
        controls={v2}
      />
    );
  }

  if (accountProvider.selected && linksConnectedWallet) {
    return (
      <>
        <Button
          {...props}
          className={clsx(props.className, "button--grey")}
          disabled>
          {children}
        </Button>
        <Warning
          color="orange"
          className="condensed mt-1"
          msg={t("privateLinksConnectedWallet")}
        />
      </>
    );
  }

  if (accountProvider.selected && privacyUnsupported) {
    return (
      <>
        <Button
          {...props}
          className={clsx(props.className, "button--grey")}
          disabled>
          {children}
        </Button>
        <Warning
          color="orange"
          className="condensed mt-1"
          msg="This operation has no private transaction mapping. Disable private mode to submit it publicly."
        />
      </>
    );
  }

  return accountProvider.selected ? (
    <TransactionButton
      msg={msg}
      simulationScope={simulationScope}
      accountProvider={submitPublic ? accountProvider : redactedAccountProvider}
      onSuccess={(x) => {
        try {
          const coin = rawMsg?.toDeposit?.();
          if (coin)
            setDeposit({
              hash: x.txHash,
              network: x.network,
              timestamp: new Date(),
              coin,
            });
        } catch (cause) {
          console.error(
            "Transaction succeeded, but deposit tracking failed",
            cause
          );
        }
        // Return asynchronous completion work to the shared receipt boundary.
        return onSuccess?.(x);
      }}
      {...props}
      {...(reviewPrivateFees
        ? {
            toastOpts: {
              ...props.toastOpts,
              loading: props.toastOpts?.loading ?? (
                <PrivateOperationSteps fallback="Submitting Transaction" />
              ),
              loadingIcon: props.toastOpts?.loadingIcon ?? (
                <PrivateToastGhost />
              ),
              successIcon: props.toastOpts?.successIcon ?? (
                <PrivateToastSuccessGhost />
              ),
              errorIcon: props.toastOpts?.errorIcon ?? (
                <PrivateToastErrorGhost />
              ),
            },
            ReviewComponent: PrivateFeeReview,
            hideSimulation: true,
            refreshSimulationOnError: true,
            simulationPaused:
              v2.status !== "ready" || privateOperationInProgress,
            pendingTransaction: v2.operation && {
              id: v2.operation.id,
              status:
                v2.operation.phase === "confirmed" ||
                v2.operation.phase === "failed"
                  ? v2.operation.phase
                  : "pending",
              txHash: v2.operation.txHash,
              message: v2.operation.message,
              finalTransaction: v2.operation.finalTransaction,
            },
          }
        : {})}>
      {children}
    </TransactionButton>
  ) : props.className?.includes("transparent") ? null : (
    <Button
      as={Link}
      className={clsx({
        [`${props.className}`]: props.className,
        "button--outline button--icon-right": true,
      })}
      to={connectTo}>
      <span>{t("connectWallet")}</span>
      <Animated
        name="connect"
        aria-label="Connect Animation"
        className="color-primary1 button-hover-color-white"
      />
    </Button>
  );
};

type TxBalanceAccountButtonProps = Omit<
  TxButtonProps,
  "signer" | "accountProvider" | "msg"
> & {
  required: {
    asset: Asset;
    amount: bigint;
  };
};

export const TxBalanceAccountButton: FC<
  TxBalanceAccountButtonProps & {
    Component?: FC<Omit<TxButtonProps, "msg">>;
  }
> = ({
  children,
  onSuccess: onSuccess,
  hideSimulation: hideSimulation,
  onSimulation,
  required,
  Component = TxButton,
  ...props
}) => {
  const { t } = useTranslation("common");
  const v2 = useRedactedV2();
  const balance = useBalanceAccount(required);
  const { accounts } = useWalletAccounts();
  const deposit = useDeposit(
    required.amount,
    required.asset,
    <p className="ml-1 color-grey fs-12">
      {t("depositInOrderTo", {
        symbol: required.asset.metadata.symbol,
        action: props.label,
      })}
    </p>
  );
  const { toRoot } = useLocale();
  const connectTo = toRoot("connect");

  if (!props.submitPublic && v2.privateSessionLocked) {
    return (
      <PrivateSessionUnlockButton
        className={props.className}
        style={props.style}
        controls={v2}
      />
    );
  }

  if (
    !accounts ||
    accounts.length === 0 ||
    accounts?.every((a) => !isThor(a.address))
  ) {
    const { className, ...rest } = props;
    return (
      <Button
        as={Link}
        {...rest}
        className={`${className} button--primary button--outline button--icon-right`}
        label={t("connectWallet")}
        to={connectTo}>
        <Animated
          name="connect"
          aria-label="Connect Animation"
          className="color-primary1 button-hover-color-white"
        />
      </Button>
    );
  }

  return balance ? (
    <Component
      {...props}
      onSuccess={onSuccess}
      hideSimulation={hideSimulation}
      onSimulation={onSimulation}>
      {children}
    </Component>
  ) : (
    <Button
      {...props}
      label={t("depositSymbolTo", {
        symbol: required.asset.metadata.symbol,
        action: props.label,
      })}
      onClick={deposit}
    />
  );
};
